import {computed, effect, inject, Injectable, NgZone, signal} from '@angular/core';
import {Database, onValue, push, ref, remove, update} from 'firebase/database';
import {FirebaseInitService} from '../shared/firebase-init.service';
import {Room} from '@models';
import {ProfileService} from '../community';

@Injectable()
export class RoomService {

	private readonly profileService = inject(ProfileService);
	private readonly currentUid = signal<string>('');
	private readonly allRooms = signal<Room[]>([]);
	private readonly rooms = computed(() => {
		const uid = this.currentUid();
		return this.allRooms().filter((room) => room.members.some(m => m === uid) || !room.isPrivate);
	});
	private hasListener = false;
	private readonly firebaseInit = inject(FirebaseInitService);
	private readonly database: Database = this.firebaseInit.database;
	private readonly ngZone: NgZone = inject(NgZone);

	private ensureRoomsListener(): void {
		if (this.hasListener) {
			return;
		}

		this.hasListener = true;
		onValue(ref(this.database, '/rooms'), (snapshot: { forEach: (callback: (childSnapshot: { key: string | null; val: () => Record<string, unknown> }) => void) => void }) => {
			this.ngZone.run(() => {
				const syncRooms: Room[] = [];
				snapshot.forEach((childSnapshot: { key: string | null; val: () => Record<string, unknown> }) => {
					if (childSnapshot.key) {
						const newRoom = new Room({
							                       ...childSnapshot.val(),
							                       uuid: childSnapshot.key
						                       });
						syncRooms.push(newRoom);
					}
				});
				this.allRooms.set(syncRooms);
			});
		});
	}

	constructor() {
		effect(() => {
			this.currentUid.set(this.profileService.profile()?.uuid ?? '');
		});
	}

	async createRoom(room: Room): Promise<{ key: string | null }> {
		room.isPrivate = true;
		const thenableRef = push(ref(this.database, '/rooms'), room.toFirebase());
		return Promise.resolve({ key: thenableRef.key });
	}

	async createAdminRoom(room: Room): Promise<{ key: string | null }> {
		const thenableRef = push(ref(this.database, '/rooms'), room.toFirebase());
		return Promise.resolve({ key: thenableRef.key });
	}

	async deleteRoom(roomId: string): Promise<void> {
		await remove(ref(this.database, `/rooms/${roomId}`));
	}

	enterRoom(room: Room, oldRoom: Room | null | undefined) {
		if (oldRoom) {
			this.leaveRoom(oldRoom);
		}

		const uid = this.currentUid();
		if (uid) {
			update(ref(this.database, `/rooms/${room.uuid}/users/`), {[uid]: 1}).then();
		}
	}

	leaveRoom(room: Room) {
		const uid = this.currentUid();
		if (uid) {
			remove(ref(this.database, `/rooms/${room.uuid}/users/${uid}`)).then();
		}
	}

	getRooms() {
		this.ensureRoomsListener();
		return this.rooms;
	}

	getAllRooms() {
		this.ensureRoomsListener();
		return this.allRooms;
	}
}
